import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  Button,
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerHeaderTitle,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Textarea
} from '@fluentui/react-components';
import { Trans, useTranslation } from 'react-i18next';

// models
import {
  CreatePersonEmailCommand,
  UpdatePersonEmailCommand
} from '../../../models/Entities/Person/Email/EmailModels';

// components
import { ErrorMessageComponent } from '../../Error/ErrorMessageComponent';

// functions
import * as ownFunctions from './PersonEmail.Form.Functions';

interface FormValidation {
  success: boolean;
  errors: string[];
}

const EmailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PersonEmailFormComponent = (props: ownFunctions.Props) => {
  const [t] = useTranslation();

  const [submitted, setSubmitted] = useState<boolean>(false);
  const [validation, setValidation] = useState<FormValidation | undefined>();

  const [email, setEmail] = useState<string>('');
  const [comments, setComments] = useState<string>('');

  useEffect(() => {
    if (props.email) {
      setEmail(props.email.email || '');
      setComments(props.email.comments || '');
    } else {
      resetForm();
    }
  }, [props.email, props.isOpen]);

  useEffect(() => {
    if (submitted && (props.successAdding || props.successUpdating)) {
      resetForm();
      props.dismissPanel();
    }
  }, [props.successAdding, props.successUpdating]);

  const resetForm = () => {
    setEmail('');
    setComments('');
    setSubmitted(false);
    setValidation(undefined);
  };

  const isEmailValid = EmailPattern.test(email.trim());

  const validate = (): boolean => {
    const errors: string[] = [];

    if (!email.trim()) {
      errors.push(t('persons.emails.form.validation.required', { defaultValue: 'El correo es obligatorio' }));
    } else if (!isEmailValid) {
      errors.push(t('persons.emails.form.validation.format', { defaultValue: 'El correo no tiene un formato válido' }));
    }

    const result: FormValidation = { success: errors.length === 0, errors };
    setValidation(result);
    return result.success;
  };

  const handleSave = () => {
    setSubmitted(true);
    if (!validate()) return;

    const payload: CreatePersonEmailCommand = {
      personId: props.PersonId,
      email: email.trim(),
      comments: comments.trim() || null
    };

    if (props.email) {
      props.UpdateEmail({ ...payload, id: props.email.id } as UpdatePersonEmailCommand);
    } else {
      props.CreateEmail(payload);
    }
  };

  const handleCancel = () => {
    props.dismissPanel();
    resetForm();
  };

  return (
    <Drawer
      open={props.isOpen}
      type="overlay"
      position="end"
      size="small"
      onOpenChange={(_, data) => {
        if (!data.open) handleCancel();
      }}
    >
      <DrawerHeader>
        <DrawerHeaderTitle>
          {props.email
            ? t('persons.emails.form.editTitle', { defaultValue: 'Editar correo electrónico' })
            : t('persons.emails.form.addTitle', { defaultValue: 'Nuevo correo electrónico' })}
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody>
        {submitted && validation && !validation.success && (
          <MessageBar intent="error" layout="multiline" style={{ marginBottom: 12 }}>
            <MessageBarBody>
              <MessageBarTitle>
                {t('persons.form.review', { defaultValue: 'Revise la información ingresada' })}
              </MessageBarTitle>
              <ul>
                {validation.errors.map((error: string, index: number) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </MessageBarBody>
          </MessageBar>
        )}

        {submitted && (props.failAdding || props.failUpdating) && props.error && (
          <ErrorMessageComponent Error={props.error} />
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Field
            label={t('persons.emails.form.email', { defaultValue: 'Correo electrónico' })}
            required
            validationMessage={
              submitted && !isEmailValid
                ? t('persons.emails.form.validation.format', {
                    defaultValue: 'El correo no tiene un formato válido'
                  })
                : undefined
            }
            validationState={submitted && !isEmailValid ? 'error' : undefined}
          >
            <Input type="email" value={email} onChange={(_, data) => setEmail(data.value || '')} />
          </Field>

          <Field label={t('persons.emails.form.comments', { defaultValue: 'Comentarios' })}>
            <Textarea value={comments} onChange={(_, data) => setComments(data.value || '')} />
          </Field>
        </div>
      </DrawerBody>

      <DrawerFooter>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-start', width: '100%' }}>
          <Button appearance="secondary" onClick={handleCancel}>
            <Trans i18nKey={'persons.form.footer.cancel'} defaultValue="Cancelar" />
          </Button>
          <Button appearance="primary" onClick={handleSave} disabled={props.isAdding || props.isUpdating}>
            <Trans i18nKey={'persons.form.footer.save'} defaultValue="Guardar" />
          </Button>
        </div>
      </DrawerFooter>
    </Drawer>
  );
};

export default connect(
  ownFunctions.mapStateToProps,
  ownFunctions.mapDispatchToProps
)(PersonEmailFormComponent as any);
